import React from 'react';
import { ArrowDown } from 'lucide-react';

const TICKER_ITEMS = [
  { symbol: 'EVBP', value: '+2.4%', dir: 'up' },
  { symbol: 'PIPELINE', value: '$18.4M', dir: 'up' },
  { symbol: 'SIGNALS', value: '+128/sec', dir: 'up' },
  { symbol: 'INTENT', value: '+42.6%', dir: 'up' },
  { symbol: 'MQL/SQL', value: '3.1×', dir: 'up' },
  { symbol: 'HOT ACCTS', value: '281', dir: 'up' },
  { symbol: 'CTR', value: '5.7%', dir: 'up' },
  { symbol: 'CAC', value: '\u201317%', dir: 'down' },
  { symbol: 'CYCLE', value: '\u221228d', dir: 'down' },
  { symbol: 'ROI', value: '99+×', dir: 'up' },
  { symbol: 'ACCURACY', value: '98%', dir: 'up' },
  { symbol: 'LATENCY', value: '1.8s', dir: 'down' },
  { symbol: 'CONFIDENCE', value: '94.2%', dir: 'up' },
  { symbol: 'CHURN', value: '\u22124.1%', dir: 'down' },
];

const INTEGRATION_BRANDS = [
  { name: 'Bombora', style: 'font-serif italic' },
  { name: '6sense', style: 'font-mono lowercase tracking-tight' },
  { name: 'HubSpot', style: 'font-sans font-semibold' },
  { name: 'Salesforce', style: 'font-serif' },
  { name: 'LinkedIn', style: 'font-sans font-semibold' },
  { name: 'ZoomInfo', style: 'font-mono uppercase tracking-widest text-sm' },
  { name: 'Marketo', style: 'font-serif italic' },
  { name: 'Segment', style: 'font-sans font-semibold lowercase' },
  { name: 'Snowflake', style: 'font-sans font-medium' },
  { name: 'Outreach', style: 'font-serif' },
  { name: 'Salesloft', style: 'font-sans font-semibold' },
  { name: 'Gong', style: 'font-serif italic' },
  { name: 'Apollo', style: 'font-mono uppercase tracking-widest text-sm' },
  { name: 'Clearbit', style: 'font-sans font-semibold' },
  { name: 'Demandbase', style: 'font-serif' },
];

/**
 * Minimal, cinematic full-viewport hero.
 * Just: "Built for B2B. Powered by AI." + animated aurora background.
 * Signal orb & everything else lives below (in dedicated sections).
 */
export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#050A1F] text-[#F1EDE4]"
    >
      {/* ==== Animated background layers ==== */}
      {/* Cinematic Wall Street / NYC skyline with slow Ken Burns pan+zoom */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div
          className="hero-city-bg absolute"
          style={{
            inset: '-6%',
            backgroundImage: 'url(/assets/wall-street.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'saturate(0.75) contrast(1.1) brightness(0.55)',
            willChange: 'transform',
          }}
        />
      </div>
      {/* base gradient wash — sits ABOVE city image to darken it & bind to navy palette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(5,10,31,0.72) 0%, rgba(10,16,36,0.55) 45%, rgba(5,10,31,0.85) 100%), radial-gradient(ellipse at 30% 40%, rgba(15,28,64,0.35) 0%, rgba(5,10,31,0.7) 60%, rgba(5,10,31,0.9) 100%)',
        }}
      />
      {/* aurora blobs */}
      <div className="hero-aurora-a pointer-events-none" />
      <div className="hero-aurora-b pointer-events-none" />
      <div className="hero-aurora-c pointer-events-none" />

      {/* moving light beams */}
      <div className="hero-beam pointer-events-none" />
      <div className="hero-beam delay pointer-events-none" />

      {/* subtle star field */}
      <div className="hero-stars pointer-events-none opacity-60" />

      {/* precision grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.045] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(78,205,196,1) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,1) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />

      {/* vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 35%, rgba(5,10,31,0.55) 100%)',
        }}
      />

      {/* ==== Foreground ==== */}
      <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 text-[10px] font-mono uppercase tracking-[0.35em] text-[#4ECDC4] mb-8">
            <span className="w-8 h-px bg-[#4ECDC4]/60" />
            <span>EvolveBPM</span>
            <span className="w-8 h-px bg-[#4ECDC4]/60" />
          </div>

          <h1 className="hero-headline leading-[0.9] tracking-[-0.03em]">
            <span className="block text-[64px] sm:text-[96px] lg:text-[144px] xl:text-[180px] font-serif italic font-light text-[#F1EDE4]">
              Built for <span className="text-[#4ECDC4]">B2B.</span>
            </span>
            <span className="block text-[64px] sm:text-[96px] lg:text-[144px] xl:text-[180px] font-serif italic font-light text-[#F1EDE4]">
              Powered by <span className="text-[#FFB020]">AI.</span>
            </span>
          </h1>

          <p className="mt-10 mx-auto max-w-2xl text-lg lg:text-xl text-white/60 font-sans font-normal leading-relaxed">
            AI-powered demand generation that finds ready buyers the moment they start looking — and turns every signal into pipeline.
          </p>
        </div>

        {/* Integration partners marquee */}
        <div className="mt-16 lg:mt-20 flex flex-col items-center gap-4">
          <div className="text-[9px] font-mono uppercase tracking-[0.35em] text-white/35">
            Integrates with
          </div>
          <div
            className="relative w-full max-w-4xl overflow-hidden marquee-mask"
            data-testid="hero-integrations-marquee"
          >
            <div className="flex gap-10 md:gap-14 marquee-track whitespace-nowrap py-1">
              {[
                // Doubled for seamless infinite loop
                ...INTEGRATION_BRANDS,
                ...INTEGRATION_BRANDS,
              ].map((b, i) => (
                <span
                  key={`${b.name}-${i}`}
                  className={`shrink-0 text-white/55 hover:text-[#4ECDC4] transition-colors text-base md:text-lg tracking-tight select-none ${b.style}`}
                >
                  {b.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <a
          href="#signal"
          className="absolute left-1/2 -translate-x-1/2 bottom-8 flex flex-col items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 hover:text-[#4ECDC4] transition-colors"
        >
          <span>Explore</span>
          <ArrowDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} />
        </a>
      </div>

      {/* Stock ticker strip — pinned bottom, above the Explore cue's visual zone */}
      <div
        className="absolute inset-x-0 bottom-20 z-10 border-y border-white/10 bg-[#050A1F]/70 backdrop-blur-md overflow-hidden marquee-mask"
        data-testid="hero-ticker"
      >
        <div className="flex gap-8 marquee-track ticker-track whitespace-nowrap py-2.5">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => {
            const isUp = t.dir === 'up';
            const color = isUp ? '#4ECDC4' : '#FF6B6B';
            const arrow = isUp ? '\u25B2' : '\u25BC';
            return (
              <span
                key={`${t.symbol}-${i}`}
                className="inline-flex items-center gap-2 shrink-0 text-[11px] font-mono uppercase tracking-widest"
              >
                <span className="text-white/60">{t.symbol}</span>
                <span style={{ color }} className="font-semibold">
                  {arrow} {t.value}
                </span>
                <span className="text-white/20">·</span>
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}
